import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SHOPS, POT_FALLBACK } from "@/data";
import { Alpana } from "@/components/Logo";
import { speakBlob, stopSpeaking } from "@/lib/voice";
import { requestTalk, pollTalk } from "@/lib/talk";
import { shopChat, repVoice, imgSrc } from "./repApi";
import ShopRep from "./ShopRep";
import Shelf from "./Shelf";
import ProductDetail from "./ProductDetail";
import TryOn from "./TryOn";
import Compare from "./Compare";
import OrderCard from "./OrderCard";

const WELCOME = "নমস্কার। আসুন।\nকী খুঁজছেন আজ?";
const DEFAULT_CHIPS = [
  { label: "একটা পাঞ্জাবি চাই", send: "একটা পাঞ্জাবি চাই" },
  { label: "উপহার চাই", send: "উপহার চাই" },
  { label: "হাতের কাজের কিছু দেখতে চাই", send: "হাতের কাজের কিছু দেখতে চাই" },
  { label: "আমি শুধু ঘুরে দেখতে চাই", send: "আমি শুধু ঘুরে দেখতে চাই" },
];
const WELCOME_LINE = "নমস্কার। আসুন। কী খুঁজছেন আজ?";

export default function PotShop({ saved, toggleSave, navTo, site }) {
  const base = SHOPS.potchitra;
  const live = (site?.products || []).filter((p) => p.shop_key === "potchitra" && p.active !== false);
  const products = live.length ? live : POT_FALLBACK;
  const shopName = site?.shops?.find((s) => s.key === "potchitra")?.name || base.name;
  const panjabis = products.filter((p) => p.tryon);

  const [line, setLine] = useState(WELCOME);
  const [chips, setChips] = useState(DEFAULT_CHIPS);
  const [busy, setBusy] = useState(false);
  const [lipVideo, setLipVideo] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("rom_rep_voice") !== "0");
  const [spotlight, setSpotlight] = useState(false);
  const [focus, setFocus] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tryonOpen, setTryonOpen] = useState(false);
  const [tryonProduct, setTryonProduct] = useState(null);
  const [results, setResults] = useState({});
  const [compareOpen, setCompareOpen] = useState(false);
  const [orderItems, setOrderItems] = useState(null);

  const historyRef = useRef([]);
  const shelfRef = useRef(null);
  const saidTryonHint = useRef({});
  const saidCompare = useRef(false);
  const lipRef = useRef(null);
  const voiceRef = useRef(voiceOn);
  voiceRef.current = voiceOn;
  lipRef.current = lipVideo;

  // স্বাগতম — ঠোঁটের ভিডিও আগে (ক্যাশ থাকলে সঙ্গে সঙ্গে), না হলে গলায়
  useEffect(() => {
    let alive = true;
    (async () => {
      const talk = await requestTalk(WELCOME_LINE, "rep");
      if (!alive) return;
      if (talk?.video_url) {
        setLipVideo(talk.video_url);
        return;
      }
      const timer = setTimeout(async () => {
        if (alive && !lipRef.current) speakLine(WELCOME);
      }, 6000);
      if (talk?.talk_id) {
        const url = await pollTalk(talk.talk_id, 300000);
        clearTimeout(timer);
        if (alive && url) setLipVideo(url);
      }
    })();
    return () => {
      alive = false;
      stopSpeaking();
    };
    // eslint-disable-next-line
  }, []);

  // ঠোঁটের ভিডিও এলে গলার অন্য স্রোত থামান — দ্বিগুণ কণ্ঠ নয়
  useEffect(() => {
    if (lipVideo) stopSpeaking();
  }, [lipVideo]);

  const speakLine = async (text) => {
    if (!voiceRef.current || !text) return;
    const blob = await repVoice(text);
    if (blob) {
      try {
        await speakBlob(blob);
      } catch {}
    }
  };

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    setBusy(true);
    stopSpeaking();
    historyRef.current.push({ role: "user", text: msg });
    const d = await shopChat({
      shop_key: "potchitra",
      message: msg,
      history: historyRef.current.slice(-6),
      focus_product_id: focus?.id || null,
      session_id: localStorage.getItem("rom_session") || "anon",
    });
    if (!d) {
      setLine("এই মুহূর্তে গলা আটকে গেছে — একটু পরে আবার বলুন।");
    } else {
      setLine(d.reply);
      setChips(d.chips || []);
      historyRef.current.push({ role: "rep", text: d.reply });
      if (d.product_ids?.length) {
        const prod = products.find((p) => p.id === d.product_ids[0]);
        if (prod) setFocus(prod);
      }
      if (d.action === "spotlight") {
        setSpotlight(true);
        setTimeout(() => shelfRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
      }
      if (d.action === "tryon") {
        const prod = products.find((p) => p.id === d.product_ids?.[0]) || focus || panjabis[0];
        setTimeout(() => openTryon(prod), 900);
      }
      if (d.action === "order") {
        const prod = products.find((p) => p.id === d.product_ids?.[0]) || focus;
        setTimeout(() => setOrderItems([prod || products[0]]), 700);
      }
      speakLine(d.reply);
    }
    setBusy(false);
  };

  const onChip = (c) => {
    if (c.action === "tryon") {
      openTryon(focus || panjabis[0]);
      return;
    }
    if (c.action === "compare") {
      setCompareOpen(true);
      return;
    }
    if (c.action === "order") {
      setOrderItems([focus || products[0]]);
      return;
    }
    send(c.send || c.label);
  };

  const openTryon = (product) => {
    if (!product) return;
    setFocus(product);
    setTryonProduct(product);
    setTryonOpen(true);
    stopSpeaking();
  };

  const openDetail = (p) => {
    setDetail(p);
    setFocus(p);
    if (p.tryon && !saidTryonHint.current[p.id]) {
      saidTryonHint.current[p.id] = true;
      setTimeout(() => {
        setLine("চাইলে নিজের উপরও দেখে নিতে পারেন।");
        setChips([
          { label: "নিজের উপর দেখি", action: "tryon" },
          { label: "থাক, দেখেই নিই", send: "আমি শুধু ঘুরে দেখতে চাই" },
        ]);
        speakLine("চাইলে নিজের উপরও দেখে নিতে পারেন।");
      }, 800);
    }
  };

  const onTryonResult = (pid, url) => {
    const next = { ...results, [pid]: url };
    setResults(next);
    const done = panjabis.filter((p) => next[p.id]).length;
    if (done >= 2 && !saidCompare.current) {
      saidCompare.current = true;
      setLine("এই দুটো আপনার পছন্দ হয়েছে।\nচাইলে আমি দুটো পাশাপাশি দেখিয়ে দিই।");
      setChips([{ label: "পাশাপাশি দেখান", action: "compare" }]);
      speakLine("এই দুটো আপনার পছন্দ হয়েছে। চাইলে আমি দুটো পাশাপাশি দেখিয়ে দিই।");
    }
  };

  const compareProducts = panjabis.filter((p) => results[p.id]).map((p) => ({ ...p, tryonUrl: results[p.id] }));

  const bargainPrompt = () => {
    setLine("বলুন তো, কত হলে নেবেন? মনের দামটা লিখে পাঠান — যেমন ₹1,700 হলে নেব।");
    setChips([
      { label: "তালিকার দামেই নেব", send: `আমি ${focus?.price || "তালিকার দামে"} নেব` },
    ]);
    document.querySelector('[data-testid="rep-input"]')?.focus();
  };

  return (
    <div>
      {/* দোকানের ভেতরে — পুরো দৃশ্য */}
      <section className="relative flex min-h-[94svh] flex-col overflow-hidden">
        <motion.img
          src={imgSrc("/api/media/shop/interior.jpg")}
          alt="পটচিত্র ঘর"
          data-testid="pot-shop-backdrop"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/10 to-dusk/30" />
        <div className="night-glow" />

        <div className="relative z-20 flex items-start justify-between p-5 pt-20 sm:p-8 sm:pt-24">
          <motion.button
            data-testid="back-to-haat"
            onClick={() => navTo("hat", "রঙের হাট")}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex items-center gap-2 rounded-full border border-sand/25 bg-dusk/40 px-4 py-1.5 text-xs text-sand/85 backdrop-blur-md transition-colors hover:border-gold/60 hover:text-gold"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            হাটের পথে ফিরুন
          </motion.button>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-right"
          >
            <p className="font-display text-2xl text-alpana drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] sm:text-3xl">{shopName}</p>
            <p className="font-latin text-[10px] italic tracking-[0.25em] text-gold/85">THE SCROLL-PAINTERS' HOUSE</p>
          </motion.div>
        </div>

        {/* চিত্রা — দৃশ্যের ভেতরে দাঁড়িয়ে */}
        <div className="pointer-events-none relative z-10 mt-auto flex justify-end px-4 sm:px-10">
          <div className="pointer-events-auto">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <ShopRep
                line={line}
                chips={chips}
                busy={busy}
                onChip={onChip}
                onSend={send}
                voiceOn={voiceOn}
                onToggleVoice={() => {
                  const nv = !voiceOn;
                  setVoiceOn(nv);
                  localStorage.setItem("rom_rep_voice", nv ? "1" : "0");
                  if (!nv) stopSpeaking();
                }}
                lipVideo={lipVideo}
                onVideoError={() => setLipVideo(null)}
                inputOpen
              />
            </motion.div>
          </div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="relative z-10 pb-4 pt-3 text-center text-[11px] text-sand/45"
        >
          নিচে নামুন — দোকানের তাক দেখুন
        </motion.p>
      </section>

      <Alpana className="mx-auto mt-10 w-28 opacity-30" />
      <Shelf
        products={products}
        saved={saved}
        toggleSave={toggleSave}
        onOpen={openDetail}
        onTryon={openTryon}
        spotlight={spotlight}
        shelfRef={shelfRef}
      />

      <AnimatePresence>
        {detail && (
          <ProductDetail
            product={detail}
            saved={saved}
            toggleSave={toggleSave}
            onClose={() => setDetail(null)}
            onTryon={(p) => {
              setDetail(null);
              openTryon(p);
            }}
            onOrder={(p) => {
              setDetail(null);
              setOrderItems([p]);
            }}
            onBargain={() => {
              setDetail(null);
              bargainPrompt();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {tryonOpen && (
          <TryOn
            open={tryonOpen}
            product={tryonProduct || panjabis[0]}
            panjabis={panjabis}
            results={results}
            onResult={onTryonResult}
            onReset={() => setResults({})}
            onSwitch={(p) => setTryonProduct(p)}
            onClose={() => setTryonOpen(false)}
            onCompare={() => {
              setTryonOpen(false);
              setCompareOpen(true);
            }}
            onSave={toggleSave}
            onOrder={(p) => {
              setTryonOpen(false);
              setOrderItems([p]);
            }}
            saved={saved}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {compareOpen && compareProducts.length >= 2 && (
          <Compare
            results={compareProducts.map((p) => p.id)}
            products={compareProducts}
            onClose={() => setCompareOpen(false)}
            onSave={toggleSave}
            onOrder={(p) => {
              setCompareOpen(false);
              setOrderItems([p]);
            }}
            saved={saved}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {orderItems && <OrderCard items={orderItems} onClose={() => setOrderItems(null)} />}
      </AnimatePresence>
    </div>
  );
}
